import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, InputAdornment, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Translate as TranslateIcon, ContentCopy,
} from '@mui/icons-material';
import api from '../../../services/api';

const LANGS = [
  { value: 'pt_CV', label: '🇨🇻 Português (CV)' },
  { value: 'en_US', label: '🇺🇸 English' },
  { value: 'fr_FR', label: '🇫🇷 Français' },
];

interface AppString { id: number; lang: string; chave: string; valor: string; updated_at: string; }

const AppStrings: React.FC = () => {
  const [rows, setRows] = useState<AppString[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState('all');
  const [dialog, setDialog] = useState<{ open: boolean; item: AppString | null }>({ open: false, item: null });
  const [form, setForm] = useState({ lang: 'pt_CV', chave: '', valor: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/parametrizacoes/app-strings');
      setRows(res.data?.data || []);
    } catch { setError('Erro ao carregar strings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ lang: 'pt_CV', chave: '', valor: '' });
    setDialog({ open: true, item: null });
  };

  const openEdit = (item: AppString) => {
    setForm({ lang: item.lang, chave: item.chave, valor: item.valor });
    setDialog({ open: true, item });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (dialog.item) {
        await api.put(`/admin/parametrizacoes/app-strings/${dialog.item.id}`, { valor: form.valor });
      } else {
        await api.post('/admin/parametrizacoes/app-strings', form);
      }
      setSuccess(dialog.item ? 'String atualizada' : 'String criada');
      setDialog({ open: false, item: null });
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number, chave: string) => {
    if (!window.confirm(`Eliminar override "${chave}"?`)) return;
    try {
      await api.delete(`/admin/parametrizacoes/app-strings/${id}`);
      setSuccess('String eliminada');
      load();
    } catch { setError('Erro ao eliminar'); }
  };

  const filtered = rows.filter(r =>
    (langFilter === 'all' || r.lang === langFilter) &&
    (r.chave.toLowerCase().includes(search.toLowerCase()) ||
     r.valor.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TranslateIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={700}>Strings da App</Typography>
            <Typography variant="caption" color="text.secondary">
              Overrides de texto — corrige traduções sem publicar nova versão
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nova String
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Info box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Cada entrada sobrepõe a string correspondente na app móvel (chave = key do GetX translations).
        A app atualiza ao arrancar — sem necessidade de novo build.
      </Alert>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Pesquisar chave ou valor..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1 }}
        />
        <TextField
          select size="small" value={langFilter}
          onChange={e => setLangFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Todos os idiomas</MenuItem>
          {LANGS.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
        </TextField>
      </Box>

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={140}>Idioma</TableCell>
                <TableCell width={260}>Chave</TableCell>
                <TableCell>Valor (override)</TableCell>
                <TableCell width={130}>Atualizado</TableCell>
                <TableCell width={90} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {rows.length === 0 ? 'Sem overrides configurados' : 'Sem resultados'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip size="small" label={LANGS.find(l => l.value === row.lang)?.label || row.lang} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <code style={{ fontSize: 12, background: 'rgba(127, 127, 127, 0.15)', padding: '2px 6px', borderRadius: 4 }}>
                          {row.chave}
                        </code>
                        <Tooltip title="Copiar chave">
                          <IconButton size="small" onClick={() => navigator.clipboard.writeText(row.chave)}>
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{row.valor}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12, color: 'text.secondary' }}>
                      {new Date(row.updated_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id, row.chave)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.item ? 'Editar String' : 'Nova String'}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select label="Idioma" value={form.lang}
            onChange={e => setForm(f => ({ ...f, lang: e.target.value }))}
            disabled={!!dialog.item}
            fullWidth size="small"
          >
            {LANGS.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Chave (GetX key)" value={form.chave}
            onChange={e => setForm(f => ({ ...f, chave: e.target.value }))}
            disabled={!!dialog.item}
            fullWidth size="small"
            placeholder="ex: hub.missions.active"
            helperText="Deve corresponder exactamente à chave usada em messages.dart"
          />
          <TextField
            label="Valor (override)" value={form.valor}
            onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
            fullWidth size="small" multiline rows={3}
            placeholder="Texto que aparece na app"
            autoFocus={!!dialog.item}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, item: null })}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleSave}
            disabled={saving || !form.chave.trim() || !form.valor.trim()}
          >
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppStrings;
